import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Settings, Layers, Info, Check, XCircle, Loader2, CheckCircle2, Palette } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { manufacturingService } from '../../../services/manufacturingService';
import toast from 'react-hot-toast';

export default function ManufacturingConfigTab() {
  const [activeTab, setActiveTab] = useState('general');
  const [newMaterial, setNewMaterial] = useState('');
  const [isTesting, setIsTesting] = useState(false);
  const [testLog, setTestLog] = useState('');
  const [testStatus, setTestStatus] = useState(null); // 'success' | 'error' | null
  const [globalMaterials, setGlobalMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [customColorsRegistry, setCustomColorsRegistry] = useState({}); // { [matName]: [] }
  const [customColorName, setCustomColorName] = useState({}); // { [matName]: '' }
  const [customColorRgb, setCustomColorRgb] = useState({}); // { [matName]: '' }
  const [customColorMetallicChecked, setCustomColorMetallicChecked] = useState({}); // { [matName]: false }
  const [customColorMetallicValue, setCustomColorMetallicValue] = useState({}); // { [matName]: '1.0' }

  const parseColor = (colorStr) => {
    try {
      if (colorStr && typeof colorStr === 'string' && colorStr.startsWith('{') && colorStr.endsWith('}')) {
        return JSON.parse(colorStr);
      }
    } catch (e) {
      console.error('Failed to parse color JSON:', colorStr);
    }
    return { name: colorStr || 'Unnamed Color', rgb: '', isMetallic: false, isPolished: false };
  };

  const deleteCustomColor = (matName, color) => {
    setCustomColorsRegistry(prev => {
      const list = prev[matName] || [];
      return { ...prev, [matName]: list.filter(c => c !== color) };
    });

    setFormData(prev => {
      const mats = prev.capabilities?.materials || [];
      const updated = mats.map(m => {
        const name = typeof m === 'string' ? m : m.name;
        if (name === matName) {
          const currentObj = typeof m === 'string' ? { name: m, colors: [] } : m;
          const currentColors = currentObj.colors || [];
          return { ...currentObj, colors: currentColors.filter(c => c !== color) };
        }
        return m;
      });
      return {
        ...prev,
        capabilities: {
          ...prev.capabilities,
          materials: updated
        }
      };
    });
  };

  const [formData, setFormData] = useState({
    supplierName: '',
    supplierType: '3d_printing',
    apiKey: '',
    secretKey: '',
    testApiKey: '',
    testSecretKey: '',
    apiEndpointLive: '',
    apiEndpointTest: '',
    isLive: false,
    isActive: false,
    notificationMethod: 'email',
    payloadTemplates: {},
    capabilities: {
      machines: [],
      materials: []
    }
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [mats, myConfig] = await Promise.all([
          manufacturingService.getMaterials(true),
          manufacturingService.getMyConfig()
        ]);
        setGlobalMaterials(mats || []);
        if (myConfig) {
          setFormData(myConfig);
          
          // Symmetrically populate customColorsRegistry with loaded custom colors from DB
          const registry = {};
          const loadedMats = myConfig.capabilities?.materials || [];
          loadedMats.forEach(m => {
            const matName = typeof m === 'object' ? m.name : m;
            const colorsList = typeof m === 'object' ? (m.colors || []) : [];
            registry[matName] = colorsList;
          });
          
          // Symmetrically restore from saved customColorsRegistry to preserve inactive colors!
          const savedRegistry = myConfig.capabilities?.customColorsRegistry || {};
          Object.keys(savedRegistry).forEach(mat => {
            const list = savedRegistry[mat] || [];
            registry[mat] = [...new Set([...(registry[mat] || []), ...list])];
          });
          
          setCustomColorsRegistry(registry);
        }
      } catch (err) {
        console.error('Failed to fetch manufacturing data:', err);
        setError('Failed to load configuration. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const payload = {
        ...formData,
        capabilities: {
          ...formData.capabilities,
          customColorsRegistry: customColorsRegistry // Symmetrically persist customColorsRegistry cleanly!
        }
      };
      await manufacturingService.saveMyConfig(payload);
      toast.success('Manufacturing configuration saved successfully');
    } catch (err) {
      setError(err?.message || 'Failed to save configuration');
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    setTestLog('Initiating connection test...');
    setTestStatus(null);
    
    try {
      // Use the service test method if available, otherwise mock for now
      // const result = await manufacturingService.testConfig(formData);
      await new Promise(resolve => setTimeout(resolve, 1500));
      const result = { success: true, log: 'Connection successful. API reached successfully.', message: 'Test Passed' };
      
      setTestLog(result.log || 'No log provided.');
      setTestStatus(result.success ? 'success' : 'error');
    } catch (err) {
      setTestLog(`Error: ${err.message}`);
      setTestStatus('error');
    } finally {
      setIsTesting(false);
    }
  };

  const addMachine = () => {
    setFormData(prev => ({
      ...prev,
      capabilities: {
        ...prev.capabilities,
        machines: [
          ...prev.capabilities.machines,
          { id: crypto.randomUUID(), name: '', type: prev.supplierType === '3d_printing' ? 'Filament Printing (FDM/FFF)' : 'CNC Machining', materials: [] }
        ]
      }
    }));
  };

  const removeMachine = (id) => {
    setFormData(prev => ({
      ...prev,
      capabilities: {
        ...prev.capabilities,
        machines: prev.capabilities.machines.filter(m => m.id !== id)
      }
    }));
  };

  const updateMachineName = (id, name) => {
    setFormData(prev => ({
      ...prev,
      capabilities: {
        ...prev.capabilities,
        machines: prev.capabilities.machines.map(m => m.id === id ? { ...m, name } : m)
      }
    }));
  };

  const updateMachineType = (id, type) => {
    setFormData(prev => ({
      ...prev,
      capabilities: {
        ...prev.capabilities,
        machines: prev.capabilities.machines.map(m => m.id === id ? { ...m, type } : m)
      }
    }));
  };

  const toggleMaterialCapability = (machineId, materialName) => {
    setFormData(prev => ({
      ...prev,
      capabilities: {
        ...prev.capabilities,
        machines: prev.capabilities.machines.map(m => {
          if (m.id !== machineId) return m;
          const materials = m.materials.includes(materialName)
            ? m.materials.filter(name => name !== materialName)
            : [...m.materials, materialName];
          return { ...m, materials };
        })
      }
    }));
  };

  const toggleSupplierMaterial = (name) => {
    setFormData(prev => {
      const existingIdx = prev.capabilities.materials.findIndex(m => (typeof m === 'string' ? m : m.name) === name);
      const isSelected = existingIdx !== -1;
      
      let updatedMaterials;
      if (isSelected) {
        updatedMaterials = prev.capabilities.materials.filter((_, i) => i !== existingIdx);
      } else {
        updatedMaterials = [...prev.capabilities.materials, { name, colors: [] }];
      }
      
      const updatedMachines = isSelected 
        ? prev.capabilities.machines.map(m => ({
            ...m,
            materials: m.materials.filter(mn => mn !== name)
          }))
        : prev.capabilities.machines;

      return {
        ...prev,
        capabilities: {
          ...prev.capabilities,
          materials: updatedMaterials,
          machines: updatedMachines
        }
      };
    });
  };

  const toggleMaterialColor = (materialName, color) => {
    setFormData(prev => ({
      ...prev,
      capabilities: {
        ...prev.capabilities,
        materials: prev.capabilities.materials.map(m => {
          const currentName = typeof m === 'string' ? m : m.name;
          if (currentName !== materialName) return m;
          
          const currentObj = typeof m === 'string' ? { name: m, colors: [] } : m;
          const currentColors = currentObj.colors || [];
          const updatedColors = currentColors.includes(color)
            ? currentColors.filter(c => c !== color)
            : [...currentColors, color];
            
          return { ...currentObj, colors: updatedColors };
        })
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-border flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b shrink-0">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Settings className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">Manufacturing Configuration</h2>
            <p className="text-xs text-muted-foreground">Configure your production API connectivity and capabilities matrix.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs font-bold uppercase tracking-wider ${formData.isActive ? 'text-green-600' : 'text-muted-foreground'}`}>
            {formData.isActive ? 'Online' : 'Offline'}
          </span>
          <button
            type="button"
            onClick={() => setFormData(p => ({ ...p, isActive: !p.isActive }))}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
              formData.isActive ? 'bg-green-500' : 'bg-muted'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                formData.isActive ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="flex px-6 shrink-0 bg-muted/30 border-b">
        <button
          onClick={() => setActiveTab('general')}
          className={`flex items-center gap-2 py-4 px-6 border-b-2 font-semibold text-sm transition-all ${
            activeTab === 'general' ? 'border-primary text-primary bg-white' : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Settings className="w-4 h-4" />
          General & API
        </button>
        <button
          onClick={() => setActiveTab('matrix')}
          className={`flex items-center gap-2 py-4 px-6 border-b-2 font-semibold text-sm transition-all ${
            activeTab === 'matrix' ? 'border-primary text-primary bg-white' : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Layers className="w-4 h-4" />
          Capabilities Matrix
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-8 space-y-8">
        {error && <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-destructive text-sm mb-6 flex items-center gap-3">
          <XCircle className="w-5 h-5" /> {error}
        </div>}

        {activeTab === 'general' && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-bold text-foreground mb-1.5">Manufacturer Display Name *</label>
                <Input
                  name="supplierName"
                  value={formData.supplierName}
                  onChange={handleChange}
                  required
                  placeholder="e.g. Acme 3D Printing"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-foreground mb-1.5">Capabilities</label>
                <Select
                  name="supplierType"
                  value={formData.supplierType}
                  onChange={(val) => setFormData(p => ({ ...p, supplierType: val }))}
                  options={[
                    { value: '3d_printing', label: '3D Print Only' },
                    { value: 'cnc', label: 'CNC Only' },
                    { value: 'both', label: 'Both' }
                  ]}
                />
              </div>
            </div>

            <div className="space-y-4 p-5 rounded-xl border bg-primary/5 border-primary/10">
              <h3 className="text-sm font-bold text-primary flex items-center gap-2">
                <Info className="w-4 h-4" />
                Order Notification Strategy
              </h3>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setFormData(p => ({ ...p, notificationMethod: 'email' }))}
                  className={`flex-1 p-3 rounded-lg border flex flex-col items-center gap-2 transition-all ${
                    formData.notificationMethod === 'email' 
                      ? 'bg-primary border-primary text-primary-foreground shadow-md' 
                      : 'bg-white border-border text-foreground hover:border-primary/50'
                  }`}
                >
                  <span className="text-sm font-bold">Email Notifications</span>
                  <span className="text-[10px] opacity-80">Manual order processing via email alerts.</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(p => ({ ...p, notificationMethod: 'api' }))}
                  className={`flex-1 p-3 rounded-lg border flex flex-col items-center gap-2 transition-all ${
                    formData.notificationMethod === 'api' 
                      ? 'bg-primary border-primary text-primary-foreground shadow-md' 
                      : 'bg-white border-border text-foreground hover:border-primary/50'
                  }`}
                >
                  <span className="text-sm font-bold">API Integration</span>
                  <span className="text-[10px] opacity-80">Automated order transmission to your API.</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Live Environment */}
              <div className={`space-y-4 p-5 rounded-xl border transition-all ${formData.isLive ? 'bg-green-50/30 border-green-500' : 'bg-muted/30 opacity-60'}`}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className={`text-sm font-bold flex items-center gap-2 ${formData.isLive ? 'text-green-800' : 'text-muted-foreground'}`}>
                    Live Environment
                  </h3>
                  <button 
                    type="button" 
                    onClick={() => setFormData(p => ({ ...p, isLive: true }))}
                    className={`text-xs font-bold px-2 py-1 rounded transition-colors ${formData.isLive ? 'bg-green-600 text-white' : 'bg-muted text-muted-foreground'}`}
                  >
                    {formData.isLive ? 'ACTIVE' : 'SELECT'}
                  </button>
                </div>
                <Input
                  label="API Endpoint"
                  name="apiEndpointLive"
                  value={formData.apiEndpointLive}
                  onChange={handleChange}
                  placeholder="https://api.yourcompany.com/v1"
                />
                <Input
                  label="API Key"
                  name="apiKey"
                  value={formData.apiKey}
                  onChange={handleChange}
                  type="password"
                />
              </div>

              {/* Test Environment */}
              <div className={`space-y-4 p-5 rounded-xl border transition-all ${!formData.isLive ? 'bg-orange-50/30 border-orange-500' : 'bg-muted/30 opacity-60'}`}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className={`text-sm font-bold flex items-center gap-2 ${!formData.isLive ? 'text-orange-800' : 'text-muted-foreground'}`}>
                    Test/Sandbox Environment
                  </h3>
                  <button 
                    type="button" 
                    onClick={() => setFormData(p => ({ ...p, isLive: false }))}
                    className={`text-xs font-bold px-2 py-1 rounded transition-colors ${!formData.isLive ? 'bg-orange-600 text-white' : 'bg-muted text-muted-foreground'}`}
                  >
                    {!formData.isLive ? 'ACTIVE' : 'SELECT'}
                  </button>
                </div>
                <Input
                  label="API Endpoint"
                  name="apiEndpointTest"
                  value={formData.apiEndpointTest}
                  onChange={handleChange}
                  placeholder="https://test-api.yourcompany.com/v1"
                />
                <Input
                  label="API Key"
                  name="testApiKey"
                  value={formData.testApiKey}
                  onChange={handleChange}
                  type="password"
                />
              </div>
            </div>

            <div className="pt-4 border-t">
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleTestConnection}
                disabled={isTesting}
                className="mb-4"
              >
                {isTesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Test API Connectivity
              </Button>
              {testLog && (
                <div className={`p-4 rounded-lg font-mono text-xs ${testStatus === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-destructive/10 text-destructive border border-destructive/20'}`}>
                  {testLog}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'matrix' && (
          <div className="space-y-8 animate-in fade-in duration-300">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Materials Selection */}
              <div className="lg:col-span-1 bg-white p-5 rounded-xl border border-border shadow-sm space-y-4">
                <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                  <Layers className="w-4 h-4 text-primary" />
                  1. Assign Materials
                </h3>
                <div className="space-y-1 max-h-96 overflow-y-auto pr-2">
                  {globalMaterials.map(mat => {
                    const matName = typeof mat === 'string' ? mat : mat.name;
                    const configMat = formData.capabilities.materials.find(m => (typeof m === 'string' ? m : m.name) === matName);
                    const isSelected = !!configMat;
                    
                    return (
                      <div key={matName} className={`flex flex-col rounded-lg border transition-all ${isSelected ? 'bg-primary/5 border-primary/20 mb-2' : 'border-transparent mb-1'}`}>
                        <label className="flex items-center justify-between px-3 py-2 cursor-pointer">
                            <span className={`text-sm ${isSelected ? 'font-bold text-primary' : 'text-foreground'}`}>{matName}</span>
                            <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleSupplierMaterial(matName)}
                                className="rounded text-primary focus:ring-primary h-4 w-4"
                            />
                        </label>
                        {isSelected && (
                          <div className="px-3 pb-3 border-t border-primary/10 mt-1 pt-2.5 space-y-3">
                            <div>
                              <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider block mb-2">Offered Colors / Finishes</span>
                              <div className="flex flex-wrap gap-1.5">
                                {(() => {
                                  const matColors = typeof mat === 'object' ? (mat.colors || []) : [];
                                  const availableColors = [...new Set([
                                    ...matColors,
                                    ...(configMat.colors || []),
                                    ...(customColorsRegistry[matName] || [])
                                  ])];
                                  
                                  return availableColors.map(color => {
                                    const colorObj = parseColor(color);
                                    const isColorSelected = configMat.colors?.includes(color);
                                    const isCustom = customColorsRegistry[matName]?.includes(color) || !matColors.includes(color);
                                    let bgColor = colorObj.rgb;
                                    if (bgColor && !bgColor.startsWith('#') && bgColor.includes(',')) {
                                      bgColor = `rgb(${bgColor})`;
                                    }
                                    
                                    return (
                                      <div 
                                        key={`${matName}-${color}`} 
                                        onClick={() => toggleMaterialColor(matName, color)}
                                        className={`flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-medium transition-all border cursor-pointer select-none ${isColorSelected ? 'bg-primary text-primary-foreground border-primary' : 'bg-white text-muted-foreground border-border'}`}
                                      >
                                        {colorObj.rgb && (
                                          <span 
                                            className="w-2.5 h-2.5 rounded-full border border-white/20 inline-block shadow-inner shrink-0" 
                                            style={{ backgroundColor: bgColor }}
                                          />
                                        )}
                                        <span>{colorObj.name}</span>
                                        {colorObj.metallic !== undefined && parseFloat(colorObj.metallic) > 0 ? (
                                          <span className="text-[8px] opacity-85 uppercase font-black px-1 bg-black/20 rounded text-white">Metal {colorObj.metallic}</span>
                                        ) : (
                                          colorObj.isMetallic && <span className="text-[8px] opacity-85 uppercase font-black px-1 bg-black/20 rounded text-white font-black">Metal</span>
                                        )}
                                        {isCustom && (
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation(); // Stop click from propagating to parent div click handler!
                                              deleteCustomColor(matName, color);
                                            }}
                                            className="ml-1 p-0.5 rounded-full hover:bg-black/20 hover:text-white transition-colors flex items-center justify-center text-[10px]"
                                            title="Delete Custom Color"
                                          >
                                            <X className="w-2.5 h-2.5" />
                                          </button>
                                        )}
                                      </div>
                                    );
                                  });
                                })()}
                              </div>
                            </div>

                            {/* Symmetrical Add Custom Color section */}
                            <div className="border-t pt-2.5 mt-2 flex flex-col sm:flex-row gap-2 items-center">
                              <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider whitespace-nowrap">Add Custom:</span>
                              <input
                                type="text"
                                placeholder="Color Name (e.g. Lime)"
                                value={customColorName[matName] || ''}
                                onChange={(e) => setCustomColorName({ ...customColorName, [matName]: e.target.value })}
                                className="text-[10px] px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-primary focus:border-primary w-full sm:w-auto flex-1 bg-white text-gray-900"
                              />
                              <input
                                type="text"
                                placeholder="RGB / Hex (Optional)"
                                value={customColorRgb[matName] || ''}
                                onChange={(e) => setCustomColorRgb({ ...customColorRgb, [matName]: e.target.value })}
                                className="text-[10px] px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-primary focus:border-primary w-full sm:w-24 bg-white text-gray-900"
                              />
                              {(() => {
                                let rgbInput = (customColorRgb[matName] || '').trim();
                                if (rgbInput) {
                                  if (!rgbInput.startsWith('#') && rgbInput.includes(',')) {
                                    rgbInput = `rgb(${rgbInput})`;
                                  }
                                  return (
                                    <span 
                                      className="w-4 h-4 rounded-full border border-gray-300 shadow-inner shrink-0 animate-in fade-in" 
                                      style={{ backgroundColor: rgbInput }}
                                      title="Live Swatch Preview"
                                    />
                                  );
                                }
                                return null;
                              })()}
                              <label className="flex items-center gap-1 text-[10px] font-black text-gray-500 cursor-pointer select-none">
                                <input
                                  type="checkbox"
                                  checked={customColorMetallicChecked[matName] || false}
                                  onChange={(e) => setCustomColorMetallicChecked({ ...customColorMetallicChecked, [matName]: e.target.checked })}
                                  className="rounded text-primary w-3 h-3 border-gray-300 focus:ring-primary"
                                />
                                <span>Metallic</span>
                              </label>
                              {customColorMetallicChecked[matName] && (
                                <input
                                  type="number"
                                  min="0"
                                  max="1"
                                  step="0.1"
                                  placeholder="Value (0-1)"
                                  value={customColorMetallicValue[matName] ?? '1.0'}
                                  onChange={(e) => setCustomColorMetallicValue({ ...customColorMetallicValue, [matName]: e.target.value })}
                                  className="text-[10px] px-1 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-primary focus:border-primary w-12 bg-white text-gray-900"
                                />
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  const nameVal = customColorName[matName]?.trim();
                                  let rgbVal = customColorRgb[matName]?.trim() || '';
                                  if (!nameVal) return;

                                  let finalName = nameVal;
                                  if (!rgbVal) {
                                    if (!finalName.toLowerCase().endsWith('(natural)')) {
                                      finalName = `${finalName} (Natural)`;
                                    }
                                  }

                                  const isMetallic = !!customColorMetallicChecked[matName];
                                  const metallicVal = isMetallic ? (parseFloat(customColorMetallicValue[matName] ?? '1.0') || 1.0) : 0;
                                  const newColorObj = { name: finalName, rgb: rgbVal, isMetallic: isMetallic, metallic: metallicVal, isPolished: false };
                                  const colorStr = JSON.stringify(newColorObj);

                                  // Append to configMat colors directly and check it
                                  setFormData(prev => {
                                    const mats = prev.capabilities?.materials || [];
                                    const updated = mats.map(m => {
                                      const name = typeof m === 'string' ? m : m.name;
                                      if (name === matName) {
                                        const currentObj = typeof m === 'string' ? { name: m, colors: [] } : m;
                                        const currentColors = currentObj.colors || [];
                                        if (!currentColors.includes(colorStr)) {
                                          return { ...currentObj, colors: [...currentColors, colorStr] };
                                        }
                                      }
                                      return m;
                                    });
                                    return {
                                      ...prev,
                                      capabilities: {
                                        ...prev.capabilities,
                                        materials: updated
                                      }
                                    };
                                  });

                                  // Append to the transient select list
                                  setCustomColorsRegistry(prev => ({
                                    ...prev,
                                    [matName]: [...(prev[matName] || []), colorStr]
                                  }));

                                  setCustomColorName({ ...customColorName, [matName]: '' });
                                  setCustomColorRgb({ ...customColorRgb, [matName]: '' });
                                  setCustomColorMetallicChecked({ ...customColorMetallicChecked, [matName]: false });
                                  setCustomColorMetallicValue({ ...customColorMetallicValue, [matName]: '1.0' });
                                }}
                                disabled={!customColorName[matName]?.trim()}
                                className="px-2.5 py-1.5 bg-slate-800 hover:bg-slate-900 text-white rounded text-[10px] font-bold disabled:opacity-50 h-[28px] flex items-center justify-center shrink-0"
                              >
                                <Plus className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Machines Setup */}
              <div className="lg:col-span-2 bg-muted/30 p-5 rounded-xl border border-border space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                    <Settings className="w-4 h-4 text-primary" />
                    2. Manage Equipment
                  </h3>
                  <Button type="button" variant="outline" size="sm" onClick={addMachine} className="bg-white">
                    <Plus className="w-4 h-4 mr-1" /> Add Machine
                  </Button>
                </div>
                <div className="space-y-3">
                  {formData.capabilities.machines.map(machine => (
                    <div key={machine.id} className="flex gap-2 items-center bg-white p-2 rounded-lg border border-border shadow-sm">
                      <input
                        placeholder="Machine Name (e.g. Stratasys F370)"
                        value={machine.name}
                        onChange={(e) => updateMachineName(machine.id, e.target.value)}
                        className="flex-1 text-sm px-3 py-2 border border-border rounded focus:ring-1 focus:ring-primary outline-none"
                      />
                      <select
                        value={machine.type || 'Filament Printing (FDM/FFF)'}
                        onChange={(e) => updateMachineType(machine.id, e.target.value)}
                        className="text-xs px-2.5 py-2 border border-border rounded focus:ring-1 focus:ring-primary outline-none bg-slate-50 font-semibold"
                      >
                        <option value="Filament Printing (FDM/FFF)">Filament Printing (FDM/FFF)</option>
                        <option value="Resin Printing (SLA/DLP/MSLA)">Resin Printing (SLA/DLP/MSLA)</option>
                        <option value="Powder Printing (SLS/MJF)">Powder Printing (SLS/MJF)</option>
                        <option value="Material Jetting (PolyJet)">Material Jetting (PolyJet)</option>
                        <option value="Binder Jetting">Binder Jetting</option>
                        <option value="CNC Machining">CNC Machining / Milling</option>
                      </select>
                      <button type="button" onClick={() => removeMachine(machine.id)} className="p-2 text-muted-foreground hover:text-destructive transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Matrix View */}
            {formData.capabilities.machines.length > 0 && formData.capabilities.materials.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-foreground">3. Production Matrix</h3>
                <div className="overflow-x-auto border rounded-xl">
                  <table className="min-w-full divide-y divide-border">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider sticky left-0 z-10 bg-muted/50 border-r">Machine \ Material</th>
                        {formData.capabilities.materials.map(mat => {
                          const matName = typeof mat === 'string' ? mat : mat.name;
                          return <th key={matName} className="px-4 py-4 text-center text-xs font-bold text-muted-foreground uppercase">{matName}</th>;
                        })}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-border">
                      {formData.capabilities.machines.map(machine => (
                        <tr key={machine.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground sticky left-0 z-10 bg-white border-r">{machine.name || 'Unnamed Machine'}</td>
                          {formData.capabilities.materials.map(mat => {
                            const matName = typeof mat === 'string' ? mat : mat.name;
                            return (
                              <td key={matName} className="px-4 py-4 text-center">
                                <button
                                  type="button"
                                  onClick={() => toggleMaterialCapability(machine.id, matName)}
                                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-all mx-auto ${machine.materials.includes(matName) ? 'bg-green-500 text-white' : 'bg-muted text-transparent border border-border'}`}
                                >
                                  <Check className="w-4 h-4" />
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end pt-6 border-t">
          <Button type="submit" disabled={saving} className="px-10">
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </div>
      </form>
    </div>
  );
}
